import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Globe, LogOut, ShieldAlert } from 'lucide-react';
import { Button } from '@components/ui';
import { useAuthStore } from '@stores/auth';
import BillingPage from '../pages/BillingPage';

/**
 * Full-screen lock shown when the tenant's subscription is EXPIRED. Replaces the
 * normal app shell entirely (no sidebar / no navigation) so an expired subscriber
 * can ONLY see and renew the subscription. Data is retained; access is restored
 * the moment the license is renewed.
 */
export default function SubscriptionLocked() {
  const { i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const companyName = user?.companyName ?? user?.display_name ?? user?.name ?? '';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };
  const toggleLang = () => void i18n.changeLanguage(isRtl ? 'en' : 'ar');

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      {/* Minimal top bar — identity + language + logout only */}
      <header className="h-16 shrink-0 border-b border-border bg-surface/80 backdrop-blur flex items-center gap-3 px-4 md:px-6">
        <div className="h-9 w-9 rounded-lg grid place-items-center bg-danger-muted text-danger">
          <ShieldAlert className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{companyName || (isRtl ? 'الاشتراك' : 'Subscription')}</div>
          <div className="text-[11px] text-danger">{isRtl ? 'الاشتراك منتهٍ' : 'Subscription expired'}</div>
        </div>
        <Button variant="ghost" size="sm" onClick={toggleLang} aria-label="language">
          <Globe className="h-4 w-4" />
          {isRtl ? 'EN' : 'AR'}
        </Button>
        <Button variant="outline" size="sm" onClick={handleLogout}>
          <LogOut className="h-4 w-4" />
          {isRtl ? 'تسجيل الخروج' : 'Logout'}
        </Button>
      </header>

      {/* Locked body — subscription / renewal only */}
      <main className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="max-w-5xl mx-auto space-y-5">
          <div className="rounded-xl border border-danger/40 bg-danger-muted/20 p-6 text-center">
            <div className="mx-auto mb-3 h-12 w-12 rounded-full grid place-items-center bg-danger/15 text-danger">
              <ShieldAlert className="h-6 w-6" />
            </div>
            <h1 className="text-lg md:text-xl font-bold">
              {isRtl ? 'انتهى اشتراكك' : 'Your subscription has expired'}
            </h1>
            <p className="mt-1 text-sm text-fg-muted max-w-xl mx-auto">
              {isRtl
                ? 'تم تعطيل الوصول إلى لوحة التحكم مؤقتاً. لمتابعة استخدام النظام، جدّد اشتراكك من الخيارات أدناه. جميع بياناتك محفوظة بالكامل.'
                : 'Dashboard access is temporarily disabled. To keep using the system, renew your subscription below. All your data is safely retained.'}
            </p>
          </div>

          {/* The full billing/subscription experience for renewal. */}
          <BillingPage />
        </div>
      </main>
    </div>
  );
}
